import { SearchableItem, SearchResult } from './types';
import { getAllSearchableItems } from './searchItems';

/**
 * Performs a search with a focus on relevant matching
 * - Prioritizes sequential word matches
 * - Handles prefix matching for job titles and names
 * - Requires all query words to match in some form
 */
export const searchItems = (query: string): SearchResult[] => {
  // Return empty array if query doesn't meet minimum length
  if (!query.trim() || query.trim().length < 3) return [];
  
  const searchableItems = getAllSearchableItems();
  const normalizedQuery = query.toLowerCase().trim();
  const queryWords = normalizedQuery.split(/\s+/).filter(word => word.length >= 2);
  
  if (queryWords.length === 0) return [];
  
  // Score each item based on how well it matches the query
  const scoredItems = searchableItems.map(item => {
    const text = item.searchableText;
    let score = 0;
    let matchedWords = 0;
    
    // Exact sequential match gets highest score
    if (text.includes(normalizedQuery)) {
      score += 100;
      matchedWords = queryWords.length; // All words matched
    } else {
      // Split the text into words for better matching
      const textWords = text.split(/\s+|,|\.|;|:|-/).filter(w => w.length > 1);
      
      // Check each query word
      for (const queryWord of queryWords) {
        let wordMatched = false;
        
        // Try to match the query word against each text word
        for (const textWord of textWords) {
          // Exact word match
          if (textWord === queryWord) {
            score += 20;
            wordMatched = true;
            break;
          }
          
          // Prefix match (e.g., "mark" matches "marketing")
          if (textWord.startsWith(queryWord)) {
            score += 15;
            wordMatched = true;
            break;
          }
          
          // Partial match with at least 4 characters (more strict than before)
          if (queryWord.length >= 4 && textWord.includes(queryWord)) {
            score += 10;
            wordMatched = true;
            break;
          }
        }
        
        if (wordMatched) {
          matchedWords++;
        }
      }
    }
    
    // If not all words matched in some form, significantly reduce the score
    if (matchedWords < queryWords.length) {
      score = score / 5;
    }
    
    // Score threshold - require a minimum relevant score
    const finalScore = score >= 10 ? score : 0;
    
    return { item, score: finalScore, matchedWords };
  });
  
  // Filter out items with no match and sort by score
  return scoredItems
    .filter(({ score }) => score > 0)
    .sort((a, b) => b.score - a.score)
    .map(({ item }) => item.toSearchResult())
    .slice(0, 5); // Back to showing top 5 most relevant results
};